/**
 * @project E-School Web
 * @author Vothana CHY
 * @date 2026-07-16
 *
 * Reusable auto-play controller for carousels and sliders.
 *
 * Bugs addressed:
 *  1. "Keeps animating while holding" — mouseleave fired while mouse is held
 *     down and dragged outside the element. Now guarded by `isDragging` ref.
 *  2. "Momentum scroll flicker" — inertia scroll after touchend caused the
 *     auto-play timer to fire while the carousel was still moving. Call
 *     `notifyScrolling()` from a scroll listener to keep resetting the timer.
 *
 * Usage:
 *   const isDragging = ref(false);
 *   const { touchHandlers, mouseHandlers, cancelResume, pauseAndScheduleResume, notifyScrolling } =
 *     useCarouselAutoPlay(startFn, stopFn, { resumeDelay: 3000, isDragging });
 *
 *   // Template:
 *   <div v-on="touchHandlers" @mouseenter="mouseHandlers.onEnter" @mouseleave="mouseHandlers.onLeave">
 *
 *   // Scroll listener (reset timer on every user scroll):
 *   el.addEventListener('scroll', () => { if (!isAutoScrolling) notifyScrolling(); }, { passive: true });
 */

import { type Ref, onUnmounted } from 'vue';

export interface UseCarouselAutoPlayOptions {
  /** Milliseconds to wait after last interaction before restarting auto-play (default: 3000) */
  resumeDelay?: number;
  /** Minimum px of movement before a touch is considered a real swipe (default: 10) */
  swipeThreshold?: number;
  /**
   * Which axis to check for the swipe threshold.
   * 'horizontal' (default) — horizontal movement only (ignores vertical page scroll).
   * 'any'                  — any direction.
   */
  axis?: 'horizontal' | 'any';
  /**
   * External ref that tracks whether a mouse/pointer drag is in progress.
   * When true, mouseleave will NOT schedule a resume — prevents the auto-play
   * from firing while the user is actively holding and dragging.
   */
  isDragging?: Ref<boolean>;
  /**
   * Callback fired immediately when the user starts interacting (touch start, mouse enter, or drag start).
   * Useful to freeze active programmatic scroll animations.
   */
  onInteractionStart?: () => void;
}

export function useCarouselAutoPlay(startFn: () => void, stopFn: () => void, options: UseCarouselAutoPlayOptions = {}) {
  const resumeDelay = options.resumeDelay ?? 3000;
  const swipeThreshold = options.swipeThreshold ?? 10;
  const axis = options.axis ?? 'horizontal';

  let resumeTimeout: ReturnType<typeof setTimeout> | null = null;

  // ─── Core pause / resume ─────────────────────────────────────────────────

  /** Cancel any pending resume and stop auto-play immediately. */
  const cancelResume = () => {
    if (resumeTimeout) {
      clearTimeout(resumeTimeout);
      resumeTimeout = null;
    }
    stopFn();
  };

  /** Stop auto-play now and restart it after `resumeDelay` ms of inactivity. */
  const pauseAndScheduleResume = () => {
    stopFn();
    if (resumeTimeout) clearTimeout(resumeTimeout);
    resumeTimeout = setTimeout(() => {
      resumeTimeout = null;
      startFn();
    }, resumeDelay);
  };

  /**
   * Call this from a scroll event listener to keep pushing the resume timer
   * forward while the element is still scrolling (e.g. during inertia/momentum
   * scroll after touchend). If auto-play is already running, this is a no-op.
   */
  const notifyScrolling = () => {
    // Only act when auto-play is paused (resumeTimeout exists or timer stopped)
    if (!resumeTimeout && options.isDragging?.value === false) return;
    // Reset the countdown so resume doesn't fire mid-momentum
    if (resumeTimeout) clearTimeout(resumeTimeout);
    stopFn();
    resumeTimeout = setTimeout(() => {
      resumeTimeout = null;
      startFn();
    }, resumeDelay);
  };

  // ─── Threshold-based touch handlers ──────────────────────────────────────

  let touchStartX = 0;
  let touchStartY = 0;
  let touchDidCancel = false;

  const onTouchStart = (e: TouchEvent) => {
    options.onInteractionStart?.();
    touchStartX = e.touches[0]?.clientX ?? 0;
    touchStartY = e.touches[0]?.clientY ?? 0;
    touchDidCancel = false;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (touchDidCancel) return;
    const dx = Math.abs((e.touches[0]?.clientX ?? 0) - touchStartX);
    const dy = Math.abs((e.touches[0]?.clientY ?? 0) - touchStartY);

    const exceeded =
      axis === 'horizontal'
        ? dx > swipeThreshold && dx > dy // horizontal swipe, not page scroll
        : dx > swipeThreshold || dy > swipeThreshold; // any direction

    if (exceeded) {
      touchDidCancel = true;
      cancelResume();
    }
  };

  const onTouchEnd = () => {
    if (touchDidCancel) {
      pauseAndScheduleResume();
    }
    touchDidCancel = false;
  };

  // ─── Mouse handlers ───────────────────────────────────────────────────────

  const onMouseEnter = () => {
    options.onInteractionStart?.();
    cancelResume();
  };

  const onMouseLeave = () => {
    // BUG FIX: if the user is holding a drag and the cursor exits the element,
    // DO NOT schedule a resume — it would fire while they're still holding.
    if (options.isDragging?.value) return;
    pauseAndScheduleResume();
  };

  // ─── Cleanup ─────────────────────────────────────────────────────────────

  onUnmounted(() => {
    if (resumeTimeout) clearTimeout(resumeTimeout);
  });

  // ─── Convenience objects for v-on spread ─────────────────────────────────

  /** Spread on the scrollable element: `<div v-on="touchHandlers">` */
  const touchHandlers = {
    touchstart: onTouchStart,
    touchmove: onTouchMove,
    touchend: onTouchEnd,
    touchcancel: onTouchEnd,
  } as const;

  /** Use on the wrapper: `@mouseenter="mouseHandlers.onEnter"` */
  const mouseHandlers = {
    onEnter: onMouseEnter,
    onLeave: onMouseLeave,
  } as const;

  return {
    touchHandlers,
    mouseHandlers,
    cancelResume,
    pauseAndScheduleResume,
    notifyScrolling,
  };
}
